import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { SectionElement } from '@components/anchor-menu/anchor-menu.component';
import { MountType } from 'icreate/common/models/ops-unit';
import {
    ClassType,
    HardwareSpecification,
    OpsWithModel,
    PT100Type,
    ResistanceType,
} from 'icreate/common/models/system-specification';
import { fromEvent, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-hardware-specifications',
    templateUrl: './hardware-specifications.component.html',
    styleUrl: './hardware-specifications.component.css',
    standalone: false,
})
export class HardwareSpecificationsComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() hardwareSpecification: HardwareSpecification;
    @Input() userCanEdit: boolean;
    @ViewChildren('section') sectionElementList: QueryList<ElementRef>;

    constructor(private cdr: ChangeDetectorRef) {}

    initialNumberOfKeyboards: number = 0;
    initialNumberOfTrackballs: number = 0;
    initialNumberOfHub: number = 0;

    currentSectionIndex: number = 0;

    sectionsList: SectionElement[] = [
        { title: 'system-specifications.system.title', index: 0 },
        { title: 'system-specifications.OPS.title', index: 1 },
        { title: 'system-specifications.input.title', index: 2 },
        { title: 'system-specifications.FU.title', index: 3 },
        { title: 'system-specifications.slots.title', index: 4 },
    ];

    private isScrollToFunction: boolean = false;
    private destroy$: Subject<void> = new Subject();

    @HostListener('scroll', [])
    @HostListener('window:scroll', [])
    onScroll(): void {
        this.updateScrollIndex();
    }

    ngOnInit() {
        this.initialNumberOfKeyboards = this.hardwareSpecification.Keyboard.qty;
        this.initialNumberOfTrackballs = this.hardwareSpecification.Trackball.qty;
        this.initialNumberOfHub = this.hardwareSpecification.HUB.qty;

        const scrollContainer = document.querySelector('.system-setting-scroll-parent');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.updateScrollIndex();
                });

            fromEvent(scrollContainer, 'scrollend')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.isScrollToFunction = false;
                });
        }
    }

    ngAfterViewInit(): void {
        this.updateScrollIndex();
        this.cdr.markForCheck();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    handleKeyboardChange(event: { name: string; value: number }) {
        this.hardwareSpecification.Keyboard.qty = event.value;
    }

    handleTrackballChange(event: { name: string; value: number }) {
        this.hardwareSpecification.Trackball.qty = event.value;
    }

    handleHubInputChange(value) {
        if (isNaN(value)) value = 0;
        this.hardwareSpecification.HUB.qty = Number(value);
    }

    getMountTypeString(opsUnit: OpsWithModel) {
        if (opsUnit.mountType == MountType.CONSOLE) return 'system-specifications.OPS.mount-type.console';
        else if (opsUnit.mountType == MountType.STAND) return 'system-specifications.OPS.mount-type.stand';
        return '';
    }

    handleScrollSection(sectionIndex: number) {
        if (!this.sectionElementList) return;
        const sections = this.sectionElementList.toArray();
        const selectedSection = sections[sectionIndex];
        const scrollContainer = document.querySelector('.system-setting-scroll-parent');

        if (!selectedSection || !scrollContainer) {
            return;
        }

        const elementRect: DOMRect = selectedSection.nativeElement.getBoundingClientRect();
        const containerRect: DOMRect = scrollContainer.getBoundingClientRect();

        const offsetWithinContainer = elementRect.top - containerRect.top + scrollContainer.scrollTop - 10;
        this.currentSectionIndex = sectionIndex;
        this.isScrollToFunction = true;
        scrollContainer.scrollTo({ top: offsetWithinContainer, behavior: 'smooth' });
        this.cdr.markForCheck();
    }

    getFuLocations(locations: string[]) {
        return locations.join(', ');
    }

    get fcuData() {
        return this.hardwareSpecification.FCU;
    }

    get hubData() {
        return this.hardwareSpecification.HUB;
    }

    get iEdgeData() {
        return this.hardwareSpecification.iEDGE;
    }

    get eapData() {
        return this.hardwareSpecification.EAP;
    }

    get eapClass() {
        if (this.hardwareSpecification.EAP.class == ClassType.ABS) 'system-specifications.eap-class.abs';
        return '-';
    }

    get inputDeviceOptions() {
        return Array.from({ length: this.hardwareSpecification.OPS.length + 1 }, (_, i) => i);
    }

    get keyboardData() {
        return this.hardwareSpecification.Keyboard;
    }

    get trackballData() {
        return this.hardwareSpecification.Trackball;
    }

    get hasPt100Wire() {
        return this.hardwareSpecification.PT100;
    }

    get pt100Wrire() {
        const wiring = this.hardwareSpecification.PT100.wiring;
        if (wiring == PT100Type.WIRE_2) return 'system-specifications.PT.wiring.2-wire';
        else if (wiring == PT100Type.WIRE_3) return 'system-specifications.PT.wiring.3-wire';
        return '';
    }

    get PT100Resitance() {
        const resistance = this.hardwareSpecification.PT100.resitance;
        if (resistance == ResistanceType.JIS) return 'system-specifications.PT.resistance.JIS';
        return '';
    }

    private updateScrollIndex() {
        if (!this.sectionElementList || this.isScrollToFunction) return;
        const sections = this.sectionElementList.toArray();
        if (!sections.length) return;

        const scrollContainer = document.querySelector('.system-setting-scroll-parent');

        const atBottom = window.innerHeight + scrollContainer.scrollTop >= scrollContainer.scrollHeight - 5;

        if (atBottom) {
            this.currentSectionIndex = sections.length - 1;
            this.cdr.markForCheck();
            return;
        }

        const viewportHeight = window.innerHeight;
        let bestIndex = 0;

        const getVisibleHeight = (rect: DOMRect): number => {
            const visibleTop = Math.max(rect.top, 0);
            const visibleBottom = Math.min(rect.bottom, viewportHeight);
            return Math.max(0, visibleBottom - visibleTop);
        };

        for (let i = 0; i < sections.length; i++) {
            const section = sections[i];
            const rect: DOMRect = section.nativeElement.getBoundingClientRect();

            if (rect.y > 0 && i == 0) {
                bestIndex = i;
                break;
            }
            if (i == 0) continue;
            const previousSection = sections[i - 1];
            const previousSectionRect: DOMRect = previousSection?.nativeElement?.getBoundingClientRect();

            const currentVisibleHeight = getVisibleHeight(rect);
            const previousVisibleHeight = getVisibleHeight(previousSectionRect);

            if (previousVisibleHeight > currentVisibleHeight) {
                bestIndex = i - 1;
                break;
            } else if (rect.y > 0) {
                bestIndex = i;
                break;
            }
        }

        this.currentSectionIndex = bestIndex;
        this.cdr.markForCheck();
    }
}
